import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Star, Plus, Trash, ArrowUp, ArrowDown, FloppyDisk,
  CircleNotch, ArrowCounterClockwise, Eye,
} from "@phosphor-icons/react";

const DEFAULT_QUESTION = {
  text_es: "",
  text_en: "",
  subtitle_es: "",
  subtitle_en: "",
  active: true,
  order: 1,
};

export default function CSATEditor({ lang, canEdit }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewLang, setPreviewLang] = useState(lang || "es");

  const load = async () => {
    try {
      const { data } = await api.get("/csat/config");
      setConfig({ ...data, questions: [...(data.questions || [])] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateQuestion = (idx, patch) => {
    setConfig((c) => {
      const qs = [...c.questions];
      qs[idx] = { ...qs[idx], ...patch };
      return { ...c, questions: qs };
    });
  };

  const addQuestion = () => {
    if ((config.questions || []).length >= 5) {
      toast.error(lang === "es" ? "Máximo 5 preguntas" : "Max 5 questions");
      return;
    }
    setConfig((c) => ({
      ...c,
      questions: [...c.questions, { ...DEFAULT_QUESTION, order: c.questions.length + 1 }],
    }));
  };

  const removeQuestion = (idx) => {
    setConfig((c) => ({ ...c, questions: c.questions.filter((_, i) => i !== idx) }));
  };

  const moveQuestion = (idx, dir) => {
    setConfig((c) => {
      const qs = [...c.questions];
      const target = idx + dir;
      if (target < 0 || target >= qs.length) return c;
      [qs[idx], qs[target]] = [qs[target], qs[idx]];
      return { ...c, questions: qs.map((q, i) => ({ ...q, order: i + 1 })) };
    });
  };

  const save = async () => {
    const activeQuestions = (config.questions || []).filter((q) => q.active);
    if (activeQuestions.length === 0) {
      toast.error(lang === "es" ? "Debe haber al menos una pregunta activa" : "At least one active question required");
      return;
    }
    for (const q of config.questions) {
      if (!q.text_es?.trim() || !q.text_en?.trim()) {
        toast.error(lang === "es" ? "Todas las preguntas requieren texto en ES y EN" : "All questions need ES and EN text");
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await api.put("/csat/config", config);
      setConfig({ ...data });
      toast.success(lang === "es" ? "Configuración CSAT guardada" : "CSAT config saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!confirm(lang === "es" ? "¿Restaurar valores por defecto?" : "Restore defaults?")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/csat/config/reset");
      setConfig({ ...data });
      toast.success(lang === "es" ? "Valores por defecto restaurados" : "Defaults restored");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-6" data-testid="csat-editor-loading">
        <CircleNotch size={14} className="animate-spin" />
        {lang === "es" ? "Cargando…" : "Loading…"}
      </div>
    );
  }

  const disabled = !canEdit || saving;

  return (
    <div className="space-y-4" data-testid="csat-editor">
      {!canEdit && (
        <div className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="csat-editor-readonly">
          {lang === "es"
            ? "Solo administradores y supervisores pueden editar. Vista de solo lectura."
            : "Only admins and supervisors can edit. Read-only view."}
        </div>
      )}

      {/* Global options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div className="flex items-center justify-between border border-border rounded-md p-3">
          <div>
            <div className="font-medium">{lang === "es" ? "Auto-mostrar al cerrar" : "Auto-show on close"}</div>
            <div className="text-[11px] text-muted-foreground">
              {lang === "es" ? "Muestra la encuesta cuando la conversación se resuelve" : "Shows survey when conversation is resolved"}
            </div>
          </div>
          <Switch
            checked={config.auto_trigger_on_close}
            disabled={disabled}
            onCheckedChange={(v) => setConfig((c) => ({ ...c, auto_trigger_on_close: v }))}
            data-testid="csat-toggle-auto"
          />
        </div>
        <div className="flex items-center justify-between border border-border rounded-md p-3">
          <div>
            <div className="font-medium">{lang === "es" ? "Permitir comentario" : "Allow comment"}</div>
            <div className="text-[11px] text-muted-foreground">
              {lang === "es" ? "Campo opcional de texto libre" : "Optional free-text field"}
            </div>
          </div>
          <Switch
            checked={config.show_comment}
            disabled={disabled}
            onCheckedChange={(v) => setConfig((c) => ({ ...c, show_comment: v }))}
            data-testid="csat-toggle-comment"
          />
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-medium uppercase text-muted-foreground">
            {lang === "es" ? "Preguntas" : "Questions"} · {config.questions.length}/5
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={addQuestion}
            disabled={disabled || config.questions.length >= 5}
            data-testid="csat-add-question"
          >
            <Plus size={12} className="mr-1" />
            {lang === "es" ? "Añadir pregunta" : "Add question"}
          </Button>
        </div>

        {config.questions.map((q, idx) => (
          <div
            key={q.id || idx}
            className="border border-border rounded-md p-4 space-y-3 bg-muted/20"
            data-testid={`csat-question-${idx}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">#{idx + 1}</Badge>
                <Switch
                  checked={q.active}
                  disabled={disabled}
                  onCheckedChange={(v) => updateQuestion(idx, { active: v })}
                  data-testid={`csat-question-active-${idx}`}
                />
                <span className="text-xs text-muted-foreground">
                  {q.active ? (lang === "es" ? "Activa" : "Active") : (lang === "es" ? "Oculta" : "Hidden")}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  disabled={disabled || idx === 0}
                  onClick={() => moveQuestion(idx, -1)}
                  data-testid={`csat-question-up-${idx}`}
                >
                  <ArrowUp size={12} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  disabled={disabled || idx === config.questions.length - 1}
                  onClick={() => moveQuestion(idx, 1)}
                  data-testid={`csat-question-down-${idx}`}
                >
                  <ArrowDown size={12} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-red-500"
                  disabled={disabled || config.questions.length === 1}
                  onClick={() => removeQuestion(idx)}
                  data-testid={`csat-question-remove-${idx}`}
                >
                  <Trash size={12} />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground block mb-1">
                  Texto (ES) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={q.text_es || ""}
                  onChange={(e) => updateQuestion(idx, { text_es: e.target.value })}
                  disabled={disabled}
                  maxLength={200}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid={`csat-question-text-es-${idx}`}
                  placeholder="¿Cómo estuvo tu experiencia?"
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground block mb-1">
                  Text (EN) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={q.text_en || ""}
                  onChange={(e) => updateQuestion(idx, { text_en: e.target.value })}
                  disabled={disabled}
                  maxLength={200}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid={`csat-question-text-en-${idx}`}
                  placeholder="How was your experience?"
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground block mb-1">
                  {lang === "es" ? "Subtítulo (ES)" : "Subtitle (ES)"}
                </label>
                <input
                  type="text"
                  value={q.subtitle_es || ""}
                  onChange={(e) => updateQuestion(idx, { subtitle_es: e.target.value })}
                  disabled={disabled}
                  maxLength={300}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid={`csat-question-subtitle-es-${idx}`}
                  placeholder="Opcional"
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground block mb-1">
                  {lang === "es" ? "Subtítulo (EN)" : "Subtitle (EN)"}
                </label>
                <input
                  type="text"
                  value={q.subtitle_en || ""}
                  onChange={(e) => updateQuestion(idx, { subtitle_en: e.target.value })}
                  disabled={disabled}
                  maxLength={300}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid={`csat-question-subtitle-en-${idx}`}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="flex items-center gap-1 text-amber-500 pt-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={16} weight="fill" />
              ))}
              <span className="ml-2 text-[10px] text-muted-foreground uppercase">
                {lang === "es" ? "Escala 1–5 estrellas" : "1–5 star scale"}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Ancillary texts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] text-muted-foreground block mb-1">
            {lang === "es" ? "Placeholder comentario (ES)" : "Comment placeholder (ES)"}
          </label>
          <input
            type="text"
            value={config.comment_prompt_es || ""}
            onChange={(e) => setConfig((c) => ({ ...c, comment_prompt_es: e.target.value }))}
            disabled={disabled}
            maxLength={200}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="csat-comment-prompt-es"
          />
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block mb-1">
            {lang === "es" ? "Placeholder comentario (EN)" : "Comment placeholder (EN)"}
          </label>
          <input
            type="text"
            value={config.comment_prompt_en || ""}
            onChange={(e) => setConfig((c) => ({ ...c, comment_prompt_en: e.target.value }))}
            disabled={disabled}
            maxLength={200}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="csat-comment-prompt-en"
          />
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block mb-1">
            {lang === "es" ? "Mensaje de agradecimiento (ES)" : "Thank-you message (ES)"}
          </label>
          <input
            type="text"
            value={config.thank_you_es || ""}
            onChange={(e) => setConfig((c) => ({ ...c, thank_you_es: e.target.value }))}
            disabled={disabled}
            maxLength={200}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="csat-thank-you-es"
          />
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block mb-1">
            {lang === "es" ? "Mensaje de agradecimiento (EN)" : "Thank-you message (EN)"}
          </label>
          <input
            type="text"
            value={config.thank_you_en || ""}
            onChange={(e) => setConfig((c) => ({ ...c, thank_you_en: e.target.value }))}
            disabled={disabled}
            maxLength={200}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="csat-thank-you-en"
          />
        </div>
      </div>

      {/* Preview */}
      <CSATPreview config={config} previewLang={previewLang} setPreviewLang={setPreviewLang} />

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <Button
          size="sm"
          variant="outline"
          onClick={resetDefaults}
          disabled={disabled}
          data-testid="csat-reset"
        >
          <ArrowCounterClockwise size={12} className="mr-1" />
          {lang === "es" ? "Restaurar defaults" : "Restore defaults"}
        </Button>
        <Button
          size="sm"
          onClick={save}
          disabled={disabled}
          data-testid="csat-save"
        >
          {saving ? <CircleNotch size={12} className="animate-spin mr-1" /> : <FloppyDisk size={12} className="mr-1" />}
          {lang === "es" ? "Guardar cambios" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function CSATPreview({ config, previewLang, setPreviewLang }) {
  const activeQuestions = (config.questions || []).filter((q) => q.active).sort((a, b) => a.order - b.order);
  const t = (esKey, enKey) => (previewLang === "es" ? esKey : enKey);

  return (
    <div className="border border-primary/20 rounded-md p-4 bg-primary/5" data-testid="csat-preview">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-xs font-medium uppercase text-primary">
          <Eye size={12} /> {previewLang === "es" ? "Vista previa cliente" : "Customer preview"}
        </div>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={previewLang === "es" ? "default" : "outline"}
            className="h-6 px-2 text-[10px]"
            onClick={() => setPreviewLang("es")}
            data-testid="csat-preview-lang-es"
          >ES</Button>
          <Button
            size="sm"
            variant={previewLang === "en" ? "default" : "outline"}
            className="h-6 px-2 text-[10px]"
            onClick={() => setPreviewLang("en")}
            data-testid="csat-preview-lang-en"
          >EN</Button>
        </div>
      </div>
      <div className="bg-card rounded-md border border-border p-4 space-y-4 max-w-sm mx-auto">
        {activeQuestions.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-4">
            {previewLang === "es" ? "Ninguna pregunta activa" : "No active questions"}
          </div>
        ) : (
          activeQuestions.map((q, i) => (
            <div key={q.id || i} className="text-center">
              <div className="font-heading font-bold text-sm">
                {t(q.text_es, q.text_en)}
              </div>
              {(q.subtitle_es || q.subtitle_en) && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  {t(q.subtitle_es, q.subtitle_en)}
                </div>
              )}
              <div className="flex items-center justify-center gap-2 mt-2 text-muted-foreground/40">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={22} weight="regular" />
                ))}
              </div>
            </div>
          ))
        )}
        {config.show_comment && activeQuestions.length > 0 && (
          <div className="text-[11px] text-muted-foreground">
            <div className="border border-input rounded-md px-3 py-2 bg-background">
              {t(config.comment_prompt_es, config.comment_prompt_en)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
